import { EDITOR_PACKAGE_VERSION } from '@/lib/sentry/editorPackageVersion';

const MAX_TAG_LEN = 128;

/**
 * Sentry tag values must be short strings. Normalizes to avoid control chars / abuse.
 */
function toSafeTagValue(value: string, fallback: string): string {
  if (typeof value !== 'string') {
    return fallback;
  }
  const trimmed = value.replace(/[\r\n\0]/g, ' ').trim();
  if (!trimmed) {
    return fallback;
  }
  return trimmed.length > MAX_TAG_LEN ? `${trimmed.slice(0, MAX_TAG_LEN - 1)}...` : trimmed;
}

type NextSentryTagApi = { setTag: (key: string, value: string) => void };
type NextSentryUserApi = {
  setUser: (user: { id?: string; username?: string } | null) => void;
  setTag: (key: string, value: string) => void;
};

function toSafeOptionalValue(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.replace(/[\r\n\0]/g, ' ').trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed.length > MAX_TAG_LEN ? `${trimmed.slice(0, MAX_TAG_LEN - 1)}...` : trimmed;
}

/**
 * Sets the editor package version on the current Sentry scope. Used right after
 * Sentry.init in the browser bundle. Never throws.
 */
export function applySentryNextjsEditorTags(Sentry: NextSentryTagApi): void {
  try {
    const v = toSafeTagValue(EDITOR_PACKAGE_VERSION, 'unknown');
    Sentry.setTag('genesys.editor_version', v);
  } catch {
    // Sentry must never break the app
  }
}

/**
 * Fire-and-forget: sets the runtime Genesys SDK (app) version when it becomes
 * available. Never throws. No-ops in non-production (matches Sentry client init).
 */
export function requestSentryNextjsSdkVersionTag(sdkVersion: string | undefined | null): void {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  const v = toSafeTagValue(sdkVersion ?? '', '');
  if (!v) {
    return;
  }
  void import('@sentry/nextjs')
    .then((Sentry) => {
      try {
        Sentry.setTag('genesys.sdk_version', v);
        try {
          Sentry.setContext('genesys', {
            editorVersion: toSafeTagValue(EDITOR_PACKAGE_VERSION, 'unknown'),
            sdkVersion: v,
          });
        } catch {
          // optional context; tags are enough
        }
      } catch {
        // ignore
      }
    })
    .catch(() => {
      // dynamic import or Sentry failed
    });
}

/**
 * Fire-and-forget: sets Sentry user identity for error correlation.
 * Includes user id and username when available and clears identity when absent.
 * Never throws. No-ops in non-production (matches Sentry client init).
 */
export function requestSentryNextjsUserIdentity(
  userId: string | undefined | null,
  userName: string | undefined | null,
): void {
  if (process.env.NODE_ENV !== 'production') {
    return;
  }
  const id = toSafeOptionalValue(userId);
  const username = toSafeOptionalValue(userName);

  void import('@sentry/nextjs')
    .then((Sentry: NextSentryUserApi) => {
      try {
        // Always write an explicit user-id tag so it's easy to filter in Sentry.
        Sentry.setTag('genesys.user_id', id ?? 'anonymous');

        if (!id && !username) {
          Sentry.setUser(null);
          return;
        }
        Sentry.setUser({
          ...(id ? { id } : {}),
          ...(username ? { username } : {}),
        });
      } catch {
        // ignore
      }
    })
    .catch(() => {
      // dynamic import or Sentry failed
    });
}
